import { io } from "socket.io-client";
import Vue from "vue";
import store from "@/store";
import utils from "@/utils/utils";

class SocketioService {
  emisor = new Vue();
  callRoom = "";

  setupSocketConnection(id, username, isInterviewer) {
    const apiUrl = `${utils.api.protocol}://${utils.api.host}:${utils.api.port}`;
    console.log(`Connecting to API at: ${apiUrl}`);

    const socket = io(
      `${utils.api.protocol}://${utils.api.host}:${utils.api.port}`,
      { withCredentials: true }
    );

    if (isInterviewer) this.sendInterviewerId(socket, id, username);
    else this.sendCandidateId(socket, id);

    socket.on("add-interviewer", (data) => {
      Vue.prototype.$socketId = data.socketId;
    });

    socket.on("add-candidate", (data) => {
      Vue.prototype.$socketId = data.socketId;
    });

    socket.on("get-notifications", (data) => {
      this.emisor.$emit(
        "notificationsSended",
        data.map((msg) => JSON.parse(msg))
      );
    });

    socket.on("cv-processing", (data) => {
      // data.msg is an array with the result object
      const result = Array.isArray(data.msg) ? data.msg[0] : data.msg;
      this.emisor.$emit("notificationsActiveSended", result);
    });

    socket.on("cv-completed", (data) => {
      // data.msg is an array with the result object
      const result = Array.isArray(data.msg) ? data.msg[0] : data.msg;
      this.emisor.$emit("notificationsActiveSended", result);
    });

    socket.on("cv-error", (data) => {
      // data.msg is an array with the result object
      const result = Array.isArray(data.msg) ? data.msg[0] : data.msg;
      this.emisor.$emit("notificationsActiveSended", result);
    });

    socket.on("analysis-progress", (data) => {
      // Emit analysis progress for real-time tracking
      this.emisor.$emit("analysisProgressUpdate", data);
    });

    socket.on("analysis-jobs-changed", (data) => {
      // Ledger change for one analysis run (back-office broadcast): feed the live queue store
      // and re-emit for components that track a single run.
      store.dispatch("analysisQueue/applyChange", data);
      this.emisor.$emit("analysisJobsChanged", data);
    });

    socket.on("analysis-queue-tick", (data) => {
      // Dispatcher tick {slots, paused, ts}: keeps the live queue capacity fresh between polls.
      store.dispatch("analysisQueue/applyTick", data);
    });

    socket.on("realtime-update", (data) => {
      // Emit realtime update for live analysis tracking
      this.emisor.$emit("realtimeUpdate", data);
    });

    socket.on("dismiss-notification", (data) => {
      this.emisor.$emit("notificationDismissed", data.response);
    });

    socket.on("call-room", (data) => {
      this.emisor.$emit("callRoomResponse", data);
    });

    socket.on("call-open", (data) => {
      this.emisor.$emit("callOpenResponse", data);
    });

    socket.on("call-close", (data) => {
      this.emisor.$emit("callCloseResponse", data);
    });

    socket.on("call-user-join", (data) => {
      this.emisor.$emit("callUserJoinResponse", data);
    });

    socket.on("call-user-opt", (data) => {
      this.emisor.$emit("callUserOptResponse", data);
    });

    socket.on("chat-user-message", (data) => {
      this.emisor.$emit("callUserChatMsgResponse", data);
    });

    socket.on("call-user-leave", (data) => {
      this.emisor.$emit("callUserLeaveResponse", data);
    });

    socket.on("call-user-finished", (data) => {
      this.emisor.$emit("callUserFinishedResponse", data);
    });

    socket.on("call-messages", (data) => {
      this.emisor.$emit("callMessagesResponse", data);
    });

    socket.on("authenticate-response", (data) => {
      this.emisor.$emit("authenticateResponse", data);
    });

    socket.on("dialogue-conversation-response", (data) => {
      this.emisor.$emit("dialogueConversationResponse", data);
    });

    socket.on("dialogue-close-response", (data) => {});

    socket.on("dialogue-partial-msg", (data) => {
      this.emisor.$emit("dialoguePartialMsgResponse", data);
    });

    socket.on("dialogue-complete-msg", (data) => {
      this.emisor.$emit("dialogCompleteMsgResponse", data);
    });

    socket.on("dialogue-contexts-response", (data) => {
      this.emisor.$emit("dialogContextsResponse", data);
    });

    socket.on("dialogue-audio-response", (data) => {});

    socket.on("dialogue-audio-msg", (data) => {
      this.emisor.$emit("dialogueAudioMsgResponse", data);
    });

    socket.on("dialogue-audio-msg-end", (data) => {
      this.emisor.$emit("dialogueAudioMsgResponseEnd", data);
    });

    socket.on("interview-validate-step-response", (data) => {
      this.emisor.$emit("interviewValidateStepResponse", data);
    });

    socket.on("dialogue-audio-transcription-response", (data) => {
      this.emisor.$emit("dialogueAudioTranscriptionResponse", data);
    });

    socket.on("interview-likert-step-response", (data) => {
      this.emisor.$emit("interviewLikertStepResponse", data);
    });

    return socket;
  }

  disconnect(socket) {
    if (socket) {
      socket.disconnect();
    }
  }

  sendInterviewerId(socket, interviewerId, interviewerUsername) {
    socket.emit("add-interviewer", {
      interviewer: { id: interviewerId, username: interviewerUsername },
    });
  }

  sendCandidateId(socket, candidateId) {
    socket.emit("add-candidate", { candidate: candidateId });
  }

  dismissNotification(socket, index) {
    socket.emit("dismiss-notification", { index: index });
  }

  createCallRoom(socket, callId) {
    socket.emit("call-room", { callId: callId });
  }

  joinCall(socket, peerId, camera, micro) {
    socket.emit("call-join", {
      peerId: peerId,
      video: camera,
      audio: micro,
      serializations: ["binary", "binary-utf8", "json"],
    });
  }

  leaveCall(socket, isCandidate) {
    socket.emit("call-leave");
    if (!isCandidate) {
      socket.emit("call-finished");
    }

    return true;
  }

  callOptions(socket, camera, micro) {
    socket.emit("call-opt", { video: camera, audio: micro, hand: false });
  }

  chatMessage(socket, message, timeSended) {
    socket.emit("call-chat-msg", { content: message, timeSended: timeSended });
  }

  retrievePreviousMessages(socket) {
    socket.emit("chat-messages");
  }

  createConversation(socket, characterId, conversationId) {
    socket.emit("dialogue-conversation", {
      characterId: characterId,
      conversationId: conversationId,
    });
  }

  deleteConversation(socket) {
    socket.emit("dialogue-close");
  }

  sendMessage(socket, message) {
    socket.emit("dialogue-message", { message: message });
  }

  sendAudio(socket, audio, engine) {
    socket.emit(`dialogue-audio-${engine}`, { audio: audio });
  }

  sendContext(socket, positionId, interviewAnalysisTypeId) {
    socket.emit("dialogue-contexts", {
      positionId: positionId,
      interviewAnalysisTypeId: interviewAnalysisTypeId,
    });
  }

  sendTest(socket) {
    socket.emit("dialogue-test", {
      test: "test",
    });
  }
  sendInterviewValidateStep(socket, body) {
    socket.emit("interview-validate-step", { data: body });
  }
  sendInterviewSpeakStep(socket, body) {
    socket.emit("interview-step", { data: body });
  }
  sendFinalMessage(socket, interviewId, positionId) {
    socket.emit("dialogue-end-interview", {
      data: { interviewId: interviewId, positionId: positionId },
    });
  }

  sendAudioQuestion(socket, audio) {
    socket.emit("dialogue-audio-transcription", { audio: audio });
  }

  sendInterviewLikertAnswer(socket, body) {
    socket.emit("interview-likert-step", { data: body });
  }
}

export default new SocketioService();
